import type { FunctionComponent } from "react";
import {
	PostStatus,
	type IPost,
} from "../../../../types/models/post.models.types";
import { Button } from "../../../../components/ui/button";
import usePostStore from "../../../../store/post.store";
import { useHandleNavigate } from "../../../../utils/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { usePostsApproveDraftEmphemeralHttp } from "../../../../hooks/http/ephemeral/campaigns/posts-approve-draft.emphemeral.http";
import { useApprovePostEphemeralHttp } from "../../../../hooks/http/ephemeral/posts/approve-post.ephemeral.http";

type AppProps = {
	post: IPost;
};

export const CampaignPostActionBrandPartial: FunctionComponent<AppProps> = ({
	post,
}) => {
	const { execute: approveDraftHttp, fetching: fetchingApproveDraft } =
		usePostsApproveDraftEmphemeralHttp();
	const { execute: approvePostHttp, fetching: fetchingApprovePost } =
		useApprovePostEphemeralHttp();
	const { removeById } = usePostStore();
	const location = useLocation();
	const { handleNavigate } = useHandleNavigate(
		location.pathname.replace(`/${post.id}`, ""),
	);
	const navigate = useNavigate();

	async function approveDraft() {
		await approveDraftHttp({
			body: null,
			pathParams: { id: post.id },
		});
		removeById(post.id);
		handleNavigate();
	}

	function rejectDraft() {
		if (location.pathname.endsWith(post.id)) {
			return navigate(`${location.pathname}/draft-reject`);
		}

		navigate(`${location.pathname}/${post.id}/draft-reject`);
	}

	async function approvePost() {
		await approvePostHttp({
			body: { message: null },
			pathParams: { id: post.id },
		});
		removeById(post.id);
		handleNavigate();
	}

	function rejectPost() {
		if (location.pathname.endsWith(post.id)) {
			return navigate(`${location.pathname}/post-reject`);
		}

		navigate(`${location.pathname}/${post.id}/post-reject`);
	}

	function getActionMetadata(post: IPost) {
		switch (post.postStatus.status) {
			case PostStatus.draft:
				return {
					button1: {
						action: approveDraft,
						fetching: fetchingApproveDraft,
						title: "Approve",
						className:
							"bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
					},
					button2: {
						action: rejectDraft,
						fetching: false,
						title: "Reject",
						className:
							"bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
					},
				};
			case PostStatus.draftApproved:
				return {
					button1: {
						action: rejectDraft,
						fetching: false,
						title: "Change to rejected",
						className:
							"bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
					},
				};
			case PostStatus.draftRejected:
				return {
					button1: {
						action: approveDraft,
						fetching: fetchingApproveDraft,
						title: "Change to approved",
						className:
							"bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
					},
				};
			case PostStatus.post:
				return {
					button1: {
						action: approvePost,
						fetching: fetchingApprovePost,
						title: "Approve",
						className:
							"bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
					},
					button2: {
						action: rejectPost,
						fetching: false,
						title: "Reject",
						className:
							"bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
					},
				};
			case PostStatus.postApproved:
				return null;
			case PostStatus.postRejected:
				return {
					button1: {
						action: approvePost,
						fetching: fetchingApprovePost,
						title: "Change to approved",
						className:
							"bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
					},
				};
			default:
				return {
					button1: {
						action: () => {},
						fetching: false,
						title: "",
						className: "hidden",
					},
				};
		}
	}

	const actionMetadata = getActionMetadata(post);
	if (!actionMetadata) return null;

	return (
		<div className="flex justify-between items-center gap-5 w-full">
			<Button
				disabled={actionMetadata.button1.fetching}
				onClick={() => {
					actionMetadata.button1.action();
				}}
				className={`flex-1 px-4 py-6 border font-semibold ${actionMetadata.button1.className}`}
			>
				{actionMetadata.button1.title}
			</Button>
			{actionMetadata.button2 && (
				<Button
					disabled={actionMetadata.button2.fetching}
					onClick={() => {
						actionMetadata.button2.action();
					}}
					className={`flex-1 px-4 py-6 border font-semibold ${actionMetadata.button2.className}`}
				>
					{actionMetadata.button2.title}
				</Button>
			)}
		</div>
	);
};
